/* eslint-disable no-multi-assign, func-names */
import Bugsnag from '@bugsnag/js'
import * as types from 'constants/action_types'
import * as kbTypes from 'subapps/kb/actions'
import * as deviceTypes from 'constants/device_types'
import * as pages from 'constants/pages'
import * as TicketListTypes from 'constants/conversation_list_types'
import * as onboardingTypes from 'subapps/onboarding/types'
import * as draftActionTypes from 'ducks/drafts/types'
import { APP_BILLING_EXPIRED } from 'ducks/billing/types'
import { FETCH_ACCOUNT_SUCCESS } from 'constants/actionTypes/account'
import * as widgetTypes from 'ducks/widgets/types'
import {
  CHANGE_USER_SUCCESS,
  CHANGE_USER_FAILURE,
  ADD_NEW_USER_FAILURE,
} from 'ducks/inAppCards/changeUser'
import {
  COPY_CUSTOMER_EMAIL,
  COPY_CUSTOMER_WEBSITE,
} from 'ducks/inAppCards/customer'
import { UPDATE_INTEGRATION_SETTINGS_RESPONSE } from 'ducks/integrations'
import { CREATE_CARD_SUCCESS as CREATE_TRELLO_CARD_SUCCESS } from 'ducks/integrations/trello'
import { CREATE_ISSUE_SUCCESS as CREATE_GITHUB_ISSUE_SUCCESS } from 'ducks/integrations/github'
import { queryIdToQuery } from 'ducks/searches/utils/query'

import { isEmpty } from 'util/arrays'
import { buildAccountForBugsnag } from 'util/bugsnag'
import { isMobile } from 'util/media_query'
import { constructSearchQueryObject, constructSearchQueryId } from 'util/search'
import storage from 'util/storage'
import { downcase } from 'util/strings'
import {
  getDefaultTicketList,
  getTicketListFromPath,
  cacheTicketListForTicket,
  cacheTicketListForPinnedSearch,
} from 'util/ticketList'
import { captureVersion } from 'util/version'
import { UPDATE_CUSTOM_PROFILE_APP_SUCCESS } from 'ducks/integrations/customProfile/types'
import { DESTROY_MAILBOX_SUCCESS } from 'ducks/mailboxes/actionTypes'

function isAndroidRunningStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
}
function isiOSRunningStandalone() {
  return 'standalone' in window.navigator && window.navigator.standalone
}

const updateFetchingStatus = (state, key, newStatus) => {
  return {
    ...state,
    fetchingStatuses: { ...state.fetchingStatuses, [key]: newStatus },
  }
}

const reducers = {}
const auth = storage.get('auth') || {}
const isStandalone = isAndroidRunningStandalone() || isiOSRunningStandalone()

function s3CatchallPath() {
  const { pathname, hash } = window.location
  if (hash !== '') return hash.substring(2)

  return pathname
}

const defaultState = {
  version: captureVersion(),
  authenticated: auth.authenticated,
  bootstrapped: false,
  currentOpenNavSection: [],
  currentTicketList: getDefaultTicketList(s3CatchallPath()),
  deviceType: isMobile() ? deviceTypes.MOBILE : deviceTypes.DESKTOP,
  editingTicketTitle: false,
  editingTicketTitleDraft: '',
  editorToolbarMessages: [],
  isMailboxSwitcherVisible: false,
  isLeftSidebarCollapsed: false,
  isRightSidebarCollapsed: storage.get('rightSidebarCollapsed') || false,
  isStandalone,
  isTicketListScrolledToTop: true,
  justClosedKeyboard: false,
  keysPressed: {},
  lastPageBeforeNewTicketModal: null,
  latestMailboxContext: storage.get('latestMailboxContext'),
  prefersExpandedReplyFormHeader: storage.get('prefersExpandedReplyFormHeader'),
  previousScrollY: undefined,
  lastTicketListScrollerScrollState: null,
  realtimeSubscribed: false,
  selectedMentionSelectorAgentId: null,
  snackbar: { open: false },
  suppressMentionSelector: false,
  tasks: [],
  ticketsCurtainVisible: false,
  token: auth.token,
  availableAccounts: [],
  printMode: false,
  accessLists: {
    mailbox: {},
    widget: {},
  },
}

reducers[types.TOGGLE_LEFT_SIDEBAR] = state => {
  return {
    ...state,
    isLeftSidebarCollapsed: !state.isLeftSidebarCollapsed,
  }
}

reducers[types.TOGGLE_RIGHT_SIDEBAR] = state => {
  const newValue = !state.isRightSidebarCollapsed
  storage.set('rightSidebarCollapsed', newValue)
  return {
    ...state,
    isRightSidebarCollapsed: newValue,
  }
}

reducers[types.SET_LEFT_SIDEBAR_OPEN_STATE] = (state, action) => {
  const { isOpen } = action.data
  return {
    ...state,
    isLeftSidebarCollapsed: !isOpen,
  }
}

reducers[types.KEY_PRESSED_CHANGE] = (state, action) => {
  return {
    ...state,
    keysPressed: {
      ...state.keysPressed,
      ...action.data.change,
    },
  }
}

reducers[types.CHANGE_NAVIGATION_VISIBILITY] = (state, action) => {
  return {
    ...state,
    navigationVisible: action.data.visible,
    ...(!action.data.visible && { isMailboxSwitcherVisible: false }),
  }
}

reducers[types.CHANGE_MAILBOX_SWITCHER_VISIBILITY] = (state, action) => ({
  ...state,
  isMailboxSwitcherVisible: action.data.visible,
})

reducers[types.FETCHED_INBOX_DATA] = state => ({
  ...state,
  bootstrappedInbox: true,
})

reducers[types.TOGGLE_NAV_SECTION] = (state, action) => {
  const newCurrentOpenNavSection = [].concat(state.currentOpenNavSection)
  const { section, leaveOpen } = action.data
  const currentIndex = newCurrentOpenNavSection.indexOf(section)
  if (currentIndex > -1) {
    if (!leaveOpen) newCurrentOpenNavSection.splice(currentIndex, 1)
  } else {
    newCurrentOpenNavSection.push(section)
  }
  return { ...state, currentOpenNavSection: newCurrentOpenNavSection }
}

function createAccount(apiAccount) {
  const {
    integrationSettings, // These are managed under the integrations branch of the store
    ...newAccount
  } = apiAccount

  // GR HACK - if its a CI build, pretend beta flag is set
  if (newAccount.subdomain.match(/^fullstacktest/)) {
    newAccount.preferences = {
      ...(newAccount.preferences || {}),
      fullstack: true,
      groove_20_beta: true,
    }
  }
  Bugsnag.addMetadata('account', buildAccountForBugsnag(newAccount))
  return newAccount
}

reducers[types.UPDATE_REPORT_SETTINGS] = (state, action) => {
  const { payload } = action
  return {
    ...state,
    account: {
      ...state.account,
      preferences: {
        ...(state.account || {}).preferences,
        ...payload.account.preferences,
      },
    },
  }
}

reducers[types.UPDATE_APP_DATA] = (state, action) => {
  const { data } = action
  const user = data.currentUser
  const account = createAccount(data.account)

  const newPinnedSearches = (user.pinned_searches || []).map(s => {
    const pinned = { ...s }
    // ensure all queries we get from server are consistent
    // with format accepted by frontend
    pinned.query = constructSearchQueryId(pinned.query)
    return pinned
  })
  // This data is stored for lookup purposes and to prevent
  // a circular reference between app and mailbox selectors
  const mailboxes = data.mailboxes || []
  const mailboxIds = mailboxes.map(mailbox => mailbox.id)
  const mailboxFolderMap = [...mailboxes].reduce((object, mailbox) => {
    const folderIds = mailbox.folders.map(folder => folder.id)
    object[mailbox.id] = folderIds // eslint-disable-line no-param-reassign
    object[mailbox.name] = folderIds // eslint-disable-line no-param-reassign
    object[downcase(mailbox.name)] = folderIds // eslint-disable-line no-param-reassign
    return object
  }, {})

  if (newPinnedSearches && newPinnedSearches[0]) {
    cacheTicketListForPinnedSearch(
      newPinnedSearches,
      state.latestMailboxContext,
      mailboxFolderMap,
      mailboxIds,
      (user.preferences || {}).unified_inbox
    )
  }

  let newState = {
    ...state,
    account,
    bootstrapped: true,
    pinnedSearches: newPinnedSearches,
    mailboxFolderMap,
    mailboxIds,
  }

  if (data.accessLists) {
    data.accessLists.forEach(list => {
      newState = updateAccessList(newState, list)
    })
  }

  return newState
}

reducers[FETCH_ACCOUNT_SUCCESS] = (state, action) => {
  let {
    payload: { account },
  } = action
  account = createAccount(account)

  return {
    ...state,
    account,
  }
}

reducers[APP_BILLING_EXPIRED] = (state, { data }) => {
  if (!data.account) {
    return state
  }
  return {
    ...state,
    ...createAccount(data.account),
  }
}

reducers[types.FETCH_FOLDER_WITH_TICKETS_SUCCESS] = (state, action) => {
  const { data } = action
  const { mailboxId } = data
  return { previousMailbox: mailboxId }
}

reducers[types.REMEMBER_PREVIOUS_MAILBOX] = function(state, action) {
  return { ...state, previousMailbox: action.data.mailboxId }
}

reducers[pages.MAIN_PAGE] = reducers[pages.MAILBOX_PAGE] = reducers[
  pages.MAILBOX_FOLDER_PAGE
] = (
  state,
  // We default to meta.location.current.query.sort (from RFR)
  // When we redirect bare search urls, we use meta.query.sort, which we
  // populate ourselves
  {
    meta: {
      location: {
        current: { pathname, query: { sort } = {} },
      },
      query: { sort: sortFromRedirect } = {},
    },
  }
) => {
  const { currentTicketList = {} } = state
  const { mailbox } = currentTicketList
  const newTicketList =
    getTicketListFromPath(pathname, mailbox, sortFromRedirect || sort) ||
    currentTicketList
  const currentMailbox = newTicketList.mailbox ? newTicketList.mailbox : null
  cacheLatestMailboxContext(currentMailbox)

  return {
    ...state,
    currentTicketList: newTicketList,
    isMailboxSwitcherVisible: false,
    previousScrollY: undefined, // so it doesn't stick around for next path
    lastTicketListScrollerScrollState: null,
  }
}

reducers[pages.FOLDER_PAGE] = (state, action) => {
  const newState = reducers[pages.MAIN_PAGE](state, action)
  const data = action.payload
  if (data) {
    const { folderId } = data

    newState.lastListPage = {
      type: 'folder',
      folderId,
    }
  }
  return newState
}

reducers[pages.SEARCH_PAGE] = (state, action) => {
  const newState = reducers[pages.MAIN_PAGE](state, action)
  const lastSearchQueryId = action.payload.term
  const query = constructSearchQueryObject(lastSearchQueryId)
  if (!query.keywords) {
    newState.lastListPage = {
      type: 'search',
      searchQueryId: lastSearchQueryId,
    }
  }
  newState.prevLocationBeforeTicketIdSearch = null
  const currentMailbox = query.mailbox ? query.mailbox[0] : null
  cacheLatestMailboxContext(currentMailbox)

  newState.isMailboxSwitcherVisible = false
  newState.previousScrollY = undefined

  if (
    state.lastTicketListScrollerScrollState &&
    state.lastTicketListScrollerScrollState.searchQueryId !== lastSearchQueryId
  ) {
    newState.lastTicketListScrollerScrollState = null
  } else {
    newState.lastTicketListScrollerScrollState = {
      ...state.lastTicketListScrollerScrollState,
    }
  }

  return newState
}

function cacheLatestMailboxContext(mailboxName) {
  return storage.set('latestMailboxContext', mailboxName)
}

reducers[pages.LOG_CONVERSATION_PAGE] = reducers[
  pages.NEW_CONVERSATION_PAGE
] = state => {
  cacheTicketListForTicket('new', state.currentTicketList)
  return state
}

reducers[pages.TICKET_PAGE] = reducers[pages.TICKET_REPLY_PAGE] = reducers[
  pages.TICKET_REPLY_CHANGESET_PAGE
] = reducers[pages.TICKET_COMMENT_PAGE] = reducers[
  pages.TICKET_FORWARD_PAGE
] = reducers[pages.TICKET_FORWARD_CHANGESET_PAGE] = (
  state,
  { payload: { id } }
) => {
  cacheTicketListForTicket(id, state.currentTicketList)

  return {
    ...state,
    previousScrollY: undefined, // so it doesn't stick around for next path
    editingTicketTitle: false,
    isMailboxSwitcherVisible: false,
    isDraftDirty: true, // assumes the form component does a doUpdateDraft!
  }
}

reducers[types.CLEAR_RETURN_PATH] = state => {
  const previousScrollY = state.returnTo ? state.returnTo.scrollY : undefined
  return { ...state, previousScrollY, returnTo: undefined }
}

reducers[types.INIT_FOLDER] = (state, { data }) => {
  return {
    ...state,
    currentTicketList: { type: TicketListTypes.FOLDER, id: data.folderId },
  }
}

reducers[types.TICKET_MATCHES_FOLDER] = (state, { data: { folderId } }) => {
  return {
    ...state,
    currentTicketList: {
      type: TicketListTypes.FOLDER,
      id: folderId,
    },
    returnTo: { path: `/folders/${folderId}` }, // fixes back arrow on mobile
  }
}

reducers[types.SET_CURRENT_TICKET_LIST] = (
  state,
  { data: { query, mailbox, sortOrder } }
) => {
  const { id: mailboxId } = mailbox || {}
  let mappedMailbox = !mailboxId ? undefined : { name: mailboxId }

  if (!mappedMailbox) {
    // attempt to get the mailbox id from the query instead
    const { mailboxId: name } = queryIdToQuery(query) || {}
    if (name) mappedMailbox = { name }
  }

  return {
    ...state,
    currentTicketList: {
      type: TicketListTypes.SEARCH,
      id: query,
      mailbox: mappedMailbox,
      sortOrder,
    },
  }
}

reducers[types.TICKET_MATCHES_SEARCH] = (
  state,
  { data: { searchQueryString } }
) => {
  return {
    ...state,
    currentTicketList: {
      ...state.currentTicketList, // preserve sortOrder
      type: TicketListTypes.SEARCH,
      id: searchQueryString,
    },
    returnTo: { path: `/search/${encodeURIComponent(searchQueryString)}` }, // fixes back arrow on mobile
  }
}

reducers[types.SET_TICKETS_CURTAIN_VISIBILITY] = (state, action) => {
  const { data } = action
  return { ...state, ticketsCurtainVisible: data.visible }
}

reducers[types.TICKET_TITLE_EDITING_START] = (state, { data }) => {
  return {
    ...state,
    editingTicketTitle: true,
    editingTicketTitleDraft: data.currentTitle,
  }
}

reducers[types.TICKET_TITLE_EDITING_STOP] = state => {
  return {
    ...state,
    editingTicketTitle: false,
    editingTicketTitleDraft: '',
  }
}

reducers[types.TICKET_TITLE_UPDATE_REQUEST] = state => {
  return {
    ...state,
    editingTicketTitle: false,
    editingTicketTitleDraft: null,
  }
}

reducers[types.TICKET_TITLE_DRAFT_CHANGED] = (state, { data: { title } }) => {
  return {
    ...state,
    editingTicketTitleDraft: title,
  }
}

reducers[types.SHOW_SNACKBAR] = (state, action) => {
  return {
    ...state,
    snackbar: {
      open: true,
      props: { ...action.data },
    },
  }
}

const showSnackbar = (state, options) => ({
  ...state,
  snackbar: {
    open: true,
    props: { ...options, duration: 3000 },
  },
})

reducers[types.MERGE_TICKETS_FAIL] = state =>
  showSnackbar(state, { message: `Failed to merge ${app.t('tickets')}` })
reducers[ADD_NEW_USER_FAILURE] = state =>
  showSnackbar(state, { message: 'Failed to create new user' })
reducers[CHANGE_USER_SUCCESS] = state =>
  showSnackbar(state, { message: 'User has been changed' })
reducers[CHANGE_USER_FAILURE] = state =>
  showSnackbar(state, { message: 'User change failed' })
reducers[types.CREATE_LABEL_SUCCESS] = state =>
  showSnackbar(state, { message: 'Tag successfully created' })
reducers[types.UPDATE_LABEL_SUCCESS] = state =>
  showSnackbar(state, { message: 'Tag successfully updated' })
reducers[types.DELETE_LABEL_SUCCESS] = state =>
  showSnackbar(state, { message: 'Tag successfully deleted' })
reducers[types.DELETE_LABEL_FAIL] = state =>
  showSnackbar(state, {
    message: 'Oops. There was a problem deleting this tag',
  })
reducers[CREATE_TRELLO_CARD_SUCCESS] = (state, { payload: { url } }) =>
  showSnackbar(state, {
    message: 'Trello card created',
    link: url,
    linkText: `View card`,
  })
reducers[CREATE_GITHUB_ISSUE_SUCCESS] = (
  state,
  {
    payload: {
      nodes: [{ url }],
    },
  }
) =>
  showSnackbar(state, {
    message: 'GitHub issue created',
    link: url,
    linkText: `View issue`,
  })
reducers[COPY_CUSTOMER_EMAIL] = state =>
  showSnackbar(state, { message: 'Email address copied to clipboard' })
reducers[COPY_CUSTOMER_WEBSITE] = state =>
  showSnackbar(state, { message: 'Website URL copied to clipboard' })
reducers[UPDATE_INTEGRATION_SETTINGS_RESPONSE] = state =>
  showSnackbar(state, { message: 'Integration settings saved' })

reducers[types.CREATE_BULK_CHANGESET_SUCCESS] = (
  state,
  { data: { snackbar } }
) => {
  if (snackbar) {
    return showSnackbar(state, { ...snackbar })
  }
  return state
}

reducers[types.HIDE_SNACKBAR] = state => ({
  ...state,
  snackbar: { open: false },
})

reducers[types.UPDATE_REALTIME_STATUS] = (state, action) => {
  const { data } = action
  return {
    ...state,
    realtimeConnectedOnce: state.realtimeConnectedOnce || data.subscribed,
    realtimeSubscribed: data.subscribed,
  }
}

reducers[types.QUEUE_EDITOR_TOOLBAR_MESSAGE] = (state, action) => {
  const { editorToolbarMessages } = state
  const lastMessage = editorToolbarMessages[editorToolbarMessages.length - 1]
  if (lastMessage) {
    let same = true
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(action.data)) {
      same = lastMessage[key] === action.data[key]
      if (!same) {
        break
      }
    }
    if (same) {
      return state
    }
  }
  const newEditorToolbarMessages = editorToolbarMessages.slice()
  newEditorToolbarMessages.push(action.data)
  return { ...state, editorToolbarMessages: newEditorToolbarMessages }
}

reducers[types.PROCESS_NEXT_EDITOR_TOOLBAR_MESSAGE] = state => {
  const { editorToolbarMessages } = state
  const newEditorToolbarMessages = editorToolbarMessages.slice(
    1,
    editorToolbarMessages.length
  )
  return { ...state, editorToolbarMessages: newEditorToolbarMessages }
}

reducers[types.CLEAR_EDITOR_TOOLBAR_MESSAGES] = state => {
  return { ...state, editorToolbarMessages: [] }
}

reducers[draftActionTypes.SAVE_REPLY_DRAFT_REQUEST] = state => {
  if (state.isDraftDirty) return state
  return {
    ...state,
    isDraftDirty: true, // only really used on mobile...
  }
}

reducers[draftActionTypes.SAVE_REPLY_DRAFT_COMPLETE] = state => {
  return {
    ...state,
    isDraftDirty: false, // only really used on mobile...
  }
}

reducers[types.SET_JUST_CLOSED_KEYBOARD] = (state, action) => {
  return { ...state, justClosedKeyboard: action.data.state }
}

reducers[types.UPDATE_APP_DEVICE_TYPE] = (
  state,
  { data: { desktop, mobile } = {} } = {}
) => {
  let { deviceType } = state
  if (desktop) deviceType = deviceTypes.DESKTOP
  if (mobile) deviceType = deviceTypes.MOBILE
  return {
    ...state,
    deviceType,
  }
}

function updateAccessList(state, list) {
  const channelId = list.channelId || list.mailboxId || ''
  const type = channelId.startsWith('wid_') ? 'widget' : 'mailbox'

  return {
    ...state,
    accessLists: {
      ...state.accessLists,
      [type]: {
        ...state.accessLists?.[type],
        [channelId]: list,
      },
    },
  }
}

function removeAccessList(state, list) {
  const channelId = list.channelId || list.mailboxId || ''
  const type = channelId.startsWith('wid_') ? 'widget' : 'mailbox'

  return {
    ...state,
    accessLists: {
      ...state.accessLists,
      [type]: {
        ...state.accessLists?.[type],
        [channelId]: undefined,
      },
    },
  }
}

// NOTE (jscheel): This is a bit of a hack. Access lists are not actually created
// on the backend for widgets, but we want to utilize the filtering for the assignment
// dropdowns in chat, and the selectors all depend on access lists. Therefore we
// emulate the access list behavior for widgets, and then scope the selectors by
// the current channel.
function createAccessListFromWidget(widget, groupsById) {
  const agentIds = new Set(widget.userIds)
  if (!isEmpty(widget.groupIds)) {
    widget.groupIds.forEach(groupId => {
      groupsById[groupId].agents.forEach(agent => {
        agentIds.add(agent.id)
      })
    })
  }

  return {
    channelId: `wid_${widget.id}`,
    agentIds: [...agentIds],
    groupIds: [...widget.groupIds],
  }
}

reducers[types.UPDATE_ACCESS_LIST] = (state, action) => {
  return updateAccessList(state, action.data)
}

reducers[types.REMOVE_ACCESS_LIST] = (state, action) => {
  return removeAccessList(state, action.data)
}

reducers[widgetTypes.CREATE_WIDGET_SUCCESS] = reducers[
  widgetTypes.UPDATE_WIDGET_SUCCESS
] = reducers[widgetTypes.DELETE_WIDGET_SUCCESS] = (
  state,
  action,
  { groups }
) => {
  const {
    payload: { widget },
  } = action

  const groupsById = groups.reduce((memo, group) => {
    // eslint-disable-next-line no-param-reassign
    memo[group.id] = group
    return memo
  }, {})

  const accessList = createAccessListFromWidget(widget, groupsById)

  const fn =
    action.type === types.DELETE_WIDGET_SUCCESS
      ? removeAccessList
      : updateAccessList

  return fn(state, accessList)
}

reducers[widgetTypes.FETCH_WIDGETS_SUCCESS] = (state, action, { groups }) => {
  const {
    payload: { widgets },
  } = action

  let newState = { ...state }

  const groupsById = groups.reduce((memo, group) => {
    // eslint-disable-next-line no-param-reassign
    memo[group.id] = group
    return memo
  }, {})

  // HACK (jscheel): The original access lists only provide agentIds because the
  // backend figures out the agent ids from the groups, so we emulate this
  // behavior here so that both behave the same way. Once we fully unite the
  // channel types, we will be able to re-address permissions.
  widgets.forEach(widget => {
    const accessList = createAccessListFromWidget(widget, groupsById)
    newState = updateAccessList(newState, accessList)
  })

  return newState
}

reducers[types.LOGIN_REQUEST] = state => {
  return updateFetchingStatus(
    {
      ...state,
      latestLoginError: null,
    },
    'authenticating',
    true
  )
}

reducers[types.TOKEN_REFRESH_REQUEST] = state => {
  return updateFetchingStatus(
    {
      ...state,
    },
    'tokenRefresh',
    true
  )
}

reducers[types.TOKEN_REFRESH_SUCCESS] = (
  state,
  { data: { token, expiresAt } }
) => {
  return updateFetchingStatus(
    {
      ...state,
      token,
      tokenExpiresAt: expiresAt,
    },
    'tokenRefresh',
    false
  )
}

reducers[types.TOKEN_REFRESH_FAILURE] = state => {
  return updateFetchingStatus(
    {
      ...state,
    },
    'tokenRefresh',
    false
  )
}

reducers[types.LOGIN_SUCCESS] = (state, { data: { token, expiresAt } }) => {
  return updateFetchingStatus(
    {
      ...state,
      authenticated: true,
      latestLoginError: null,
      otpChallenged: false,
      otpIdentifier: null,
      userId: null,
      token,
      tokenExpiresAt: expiresAt,
      availableAccounts: [],
    },
    'authenticating',
    false
  )
}

reducers[types.LOGIN_CHALLENGE] = (
  state,
  { data: { token, otpIdentifier } }
) => {
  return updateFetchingStatus(
    {
      ...state,
      latestLoginError: state.otpChallenged ? 'Invalid code' : null, // NOTE (jscheel): If already challenged, assume that an invalid code was provided
      otpChallenged: true,
      otpIdentifier,
      userId: null,
      token,
    },
    'authenticating',
    false
  )
}

reducers[pages.LOGIN_MULTIPLE_ACCOUNTS_PAGE] = (
  state,
  { payload: { accounts } }
) => {
  return {
    ...state,
    availableAccounts: accounts,
    latestLoginError: null,
    userId: null,
  }
}

reducers[pages.LOGIN_AGENT_MISSING_PAGE] = reducers[types.LOGIN_ERROR] = (
  state,
  action
) => {
  return updateFetchingStatus(
    {
      ...state,
      latestLoginError: action && action.data && action.data.error,
      otpChallenged: false,
      otpIdentifier: null,
      userId: null,
      availableAccounts: [],
    },
    'authenticating',
    false
  )
}

// Clear reset password and forgot subdomain state
reducers[pages.LOGIN_PAGE] = state => {
  return {
    ...state,
    forgotIsSent: false,
    forgotLastMessage: '',
  }
}
reducers[pages.FORGOT_SUBDOMAIN_PAGE] = reducers[pages.LOGIN_PAGE]
reducers[pages.FORGOT_PASSWORD_PAGE] = reducers[pages.LOGIN_PAGE]

reducers[types.FORGOT_PASSWORD_SUCCESS] = (
  state,
  { data: { message, valid } }
) => {
  return updateFetchingStatus(
    {
      ...state,
      forgotIsSent: valid,
      forgotLastMessage: message,
    },
    'forgot',
    false
  )
}

reducers[types.FORGOT_PASSWORD_FAIL] = reducers[types.FORGOT_PASSWORD_SUCCESS]
reducers[types.FORGOT_SUBDOMAIN_SUCCESS] =
  reducers[types.FORGOT_PASSWORD_SUCCESS]
reducers[types.FORGOT_SUBDOMAIN_FAIL] = reducers[types.FORGOT_PASSWORD_SUCCESS]

reducers[types.LOAD_RESET_PASSWORD_REQUEST] = state => {
  return updateFetchingStatus(
    {
      ...state,
      latestChangePasswordError: null,
      changePasswordUserId: null,
      changePasswordRequire2fa: null,
      changePasswordMissingToken: false,
    },
    'changepassword',
    true
  )
}

reducers[types.LOAD_RESET_PASSWORD_SUCCESS] = (
  state,
  { data: { id, require2fa } }
) => {
  return updateFetchingStatus(
    {
      ...state,
      changePasswordUserId: id,
      changePasswordRequire2fa: require2fa,
      changePasswordMissingToken: false,
    },
    'changepassword',
    false
  )
}

reducers[types.LOAD_RESET_PASSWORD_FAIL] = (
  state,
  { data: { message, missingToken } }
) => {
  return updateFetchingStatus(
    {
      ...state,
      latestChangePasswordError: message,
      changePasswordMissingToken: missingToken,
    },
    'changepassword',
    false
  )
}

reducers[types.CHANGE_PASSWORD_REQUEST] = state => {
  return {
    ...state,
    latestChangePasswordError: null,
  }
}

reducers[types.CHANGE_PASSWORD_FAIL] = (state, { data: { message } }) => {
  return {
    ...state,
    latestChangePasswordError: message,
  }
}

reducers[types.LOGOUT] = state => {
  const authLessDefaultState = { ...state, defaultState }
  authLessDefaultState.authenticated = false
  return authLessDefaultState
}

reducers[types.LIST_SCROLL_TOP_CHANGED] = (
  state,
  { data: { isTicketListScrolledToTop } }
) => {
  return {
    ...state,
    isTicketListScrolledToTop,
  }
}

reducers[types.UPDATE_CURRENT_TICKET_SEARCH_QUERY] = state => {
  return {
    ...state,
    ticketListBeforeSearch: state.currentTicketList,
  }
}

reducers[types.RENDER_TICKET_LIST_BEFORE_SEARCH] = state => {
  return {
    ...state,
    currentTicketList: state.ticketListBeforeSearch || state.currentTicketList,
    ticketListBeforeSearch: null,
  }
}

reducers[types.PIN_SEARCH_FAILURE] = state => {
  return updateFetchingStatus(state, 'pinSearch', false)
}

reducers[types.PIN_SEARCH_SUCCESS] = (state, { data }) => {
  const pinnedSearches = state.pinnedSearches || []
  const newPinnedSearches = [].concat(pinnedSearches)
  newPinnedSearches.push(data)
  updateFetchingStatus(state, 'pinSearch', false)

  const newState = {
    ...state,
    pinnedSearches: newPinnedSearches,
  }
  return updateFetchingStatus(newState, 'pinSearch', false)
}

reducers[types.UNPIN_SEARCH_SUCCESS] = (state, { data: { id } }) => {
  const pinnedSearches = state.pinnedSearches || []
  const newPinnedSearches = pinnedSearches.filter(s => s.id !== id)

  const newState = {
    ...state,
    pinnedSearches: newPinnedSearches,
  }
  return updateFetchingStatus(newState, 'pinSearch', false)
}

reducers[types.UPDATE_AGENT_MENTION] = (state, action) => {
  const mention = action.data
  if (mention !== state.agentMention) {
    let reset = {}
    if (!mention) {
      reset = {
        selectedMentionSelectorAgentId: null,
        suppressMentionSelector: false,
      }
    }
    return {
      ...state,
      ...reset,
      agentMention: mention,
    }
  }
  return state
}

reducers[types.UPDATE_SELECTED_MENTION_SELECTOR_AGENT_ID] = (state, action) => {
  const agentId = action.data
  if (agentId !== state.selectedMentionSelectorAgentId) {
    return {
      ...state,
      selectedMentionSelectorAgentId: agentId,
    }
  }
  return state
}

reducers[types.UPDATE_SUPPRESS_MENTION_SELECTOR] = (state, action) => {
  if (action.data !== state.suppressMentionSelector) {
    return {
      ...state,
      suppressMentionSelector: action.data,
    }
  }
  return state
}

reducers[pages.TICKET_COMMENT_PAGE] = state => {
  return {
    ...state,
    suppressMentionSelector: false,
    agentMention: null,
    selectedMentionSelectorAgentId: null,
  }
}

// _PAGE reducers

reducers[pages.AUTH_CALLBACK_PAGE] = (state, action) => {
  const {
    meta: {
      query: { access_token: token },
    },
  } = action
  return {
    ...state,
    authenticated: true,
    token,
  }
}

reducers[pages.LOGOUT_PAGE] = state => {
  return {
    ...state,
    authenticated: false,
    token: null,
  }
}

reducers[types.MAILBOX_SEARCH_INPUT] = (state, action) => {
  const { term } = action.data
  if (term === state.mailboxSearchInput) return state
  return {
    ...state,
    mailboxSearchInput: term,
  }
}

// Created as a helper to test UI for changes in beta flag
reducers[types.ACCOUNT_PREFS_CHANGED] = (state, { data }) => ({
  ...state,
  account: {
    ...state.account,
    preferences: {
      ...state.account.preferences,
      ...data,
    },
  },
})

reducers[kbTypes.CREATE_KNOWLEDGE_BASE_SUCCESS] = (state, action) => {
  const { createdKnowledgeBase } = action.data

  if (createdKnowledgeBase.primary !== true) {
    return state
  }

  return {
    ...state,
    account: {
      ...state.account,
      primary_knowledge_base_id: createdKnowledgeBase.id,
    },
  }
}

reducers[kbTypes.UPDATE_KNOWLEDGE_BASE_SUCCESS] = (state, action) => {
  const { updatedKnowledgeBase } = action.data

  // Primary is unchanged
  if (
    state.account.primary_knowledge_base_id === updatedKnowledgeBase.id ||
    updatedKnowledgeBase.primary !== true
  ) {
    return state
  }

  return {
    ...state,
    account: {
      ...state.account,
      primary_knowledge_base_id: updatedKnowledgeBase.id,
    },
  }
}

reducers[onboardingTypes.SIGNUP_SUCCESS] = (state, action) => {
  const { account, user } = action.payload
  return {
    ...state,
    account: {
      ...account,
    },
    token: user.token,
  }
}

reducers[onboardingTypes.ONBOARDING_COMPLETE_SUCCESS] = state => {
  return {
    ...state,
    account: {
      ...state.account,
      is_onboarding_completed: true,
    },
  }
}

reducers[types.SEARCH_REFRESH_START] = state => {
  return {
    ...state,
    isRefreshingSearches: true,
  }
}

reducers[types.SEARCH_REFRESH_FINISH] = state => {
  return {
    ...state,
    isRefreshingSearches: false,
  }
}

reducers[types.UPDATE_LAST_TICKET_LIST_SCROLLER_SCROLL_STATE] = (
  state,
  { payload: { scrollTop, searchQueryId } }
) => {
  if (scrollTop === null) {
    return {
      ...state,
      lastTicketListScrollerScrollState: null,
    }
  }
  return {
    ...state,
    lastTicketListScrollerScrollState: {
      scrollTop,
      searchQueryId,
    },
  }
}

reducers[pages.OTP_CHALLENGE_PAGE] = reducers[types.ADD_RETURN_TO] = reducers[
  types.LOGIN_PAGE
] = (state, action) => {
  const { payload: { returnTo } = {} } = action || {}

  if (!returnTo) return state

  return {
    ...state,
    returnTo,
  }
}

reducers[types.CLEAR_RETURN_TO] = state => {
  return {
    ...state,
    returnTo: null,
  }
}

reducers[types.TOGGLE_PIN_EXPANDED_REPLY_FORM_HEADER] = state => {
  const newState = !state.prefersExpandedReplyFormHeader

  storage.set('prefersExpandedReplyFormHeader', newState)

  return {
    ...state,
    prefersExpandedReplyFormHeader: newState,
  }
}

reducers[types.TICKET_ID_SEARCH_REDIRECT] = (state, { payload: { prev } }) => {
  if (!prev) {
    return state
  }
  return {
    ...state,
    prevLocationBeforeTicketIdSearch: prev,
  }
}

reducers[types.TOGGLE_PRINT_MODE] = (state, action) => {
  const { printMode } = action.payload

  return {
    ...state,
    printMode,
  }
}

reducers[types.UPDATE_ACCOUNT_SUCCESS] = (state, action) => {
  const { updateAccount: account } = action.payload
  return {
    ...state,
    account: {
      ...state.account,
      ...account,
      preferences: {
        ...state.account.preferences,
        ...account.preferences,
      },
    },
  }
}

reducers[UPDATE_CUSTOM_PROFILE_APP_SUCCESS] = (state, action) => {
  const { enabled } = action.payload
  return {
    ...state,
    account: {
      ...state.account,
      preferences: {
        ...state.account.preferences,
        custom_profile_app_installed: enabled,
      },
    },
  }
}

reducers[DESTROY_MAILBOX_SUCCESS] = (
  state,
  { payload: { id: mailboxId } = {} }
) => {
  const { currentTicketList: { id: searchQuery } = {} } = state
  const query = constructSearchQueryObject(searchQuery)
  if (query && query.mailbox && query.mailbox.includes(mailboxId)) {
    storage.remove('latestMailboxContext')
    storage.remove('ticketListForLastViewedTicket')
    storage.remove('ticketListFromFirstPinnedSearch')
    storage.remove('lastSearchQueryId')
    return {
      ...state,
      currentTicketList: getDefaultTicketList(s3CatchallPath()),
      latestMailboxContext: null,
      isMailboxSwitcherVisible: false,
      previousScrollY: undefined, // so it doesn't stick around for next path
      lastTicketListScrollerScrollState: null,
      lastSearchQueryId: null,
    }
  }
  return state
}

export default function reducer(
  state = defaultState,
  action,
  sharedState = {}
) {
  // this is here because a long reducer with many ifs is unreadable
  const handler = reducers[action.type]
  if (handler) return handler(state, action, sharedState)
  if (action.type === types.MARK_UPDATE_AVAILABLE) {
    const { data } = action
    return { ...state, updateAvailable: data.available }
  }
  return state
}
